import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../lib/cn';
import { Logo } from './Logo';
import { ThemePicker } from './ThemePicker';

/** Decorative backdrop shared by menu-like pages. */
export function Backdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
      <div className="absolute inset-0 bg-ink-950" />
      <div className="absolute -top-40 -left-32 size-[38rem] rounded-full bg-primary-500/14 blur-[120px]" />
      <div className="absolute top-1/3 -right-40 size-[34rem] rounded-full bg-info-400/10 blur-[120px]" />
      <div className="absolute -bottom-48 left-1/4 size-[30rem] rounded-full bg-highlight-400/8 blur-[120px]" />
      <div className="absolute inset-0 [background-image:radial-gradient(rgb(255_255_255/0.07)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
    </div>
  );
}

export function PageShell({
  children,
  className,
  header = true,
  actions,
}: {
  children: ReactNode;
  className?: string;
  header?: boolean;
  actions?: ReactNode;
}) {
  return (
    <div className="relative min-h-full">
      <Backdrop />
      {header && (
        <header className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/" className="rounded-xl transition hover:opacity-80" aria-label="Home">
            <Logo size="sm" />
          </Link>
          <div className="flex items-center gap-2">
            {actions}
            <ThemePicker />
          </div>
        </header>
      )}
      <main className={cn('mx-auto max-w-6xl px-4 pb-16 sm:px-6', className)}>{children}</main>
    </div>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('glass rounded-3xl p-5 sm:p-6', className)}>{children}</div>;
}

export function ErrorBanner({ message, className }: { message: string | null; className?: string }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className={cn('rounded-2xl bg-primary-500/12 px-4 py-3 text-sm font-medium text-primary-300 ring-1 ring-primary-500/30', className)}
    >
      {message}
    </div>
  );
}
